"use strict";

import { ObjectCreator } from "../objectCreator.js";
import { Application } from "../../main/application.js";
import { TransformHandler } from "../transformHandler.js";
import { Screen } from "../../systems/core/screen.js";
import { Vector3 } from "../../math/vectors.js";

export class Camera3D extends PIXI.projection.Camera3d {
    static __main;
    static get main() { return Camera3D.__main; }

    constructor(config) {
        super();
        this.__objectType = "GameObject";

        this.name = config.name;

        // Create Parenting
        ObjectCreator.createParenting(this, config);

        // Create Anchoring
        ObjectCreator.createAnchoring(this, false, true, config);

        // Set Scale
        ObjectCreator.setScale(this, config);

        // Set Position
        ObjectCreator.setPosition(this, true, config);
        this._refreshPosition();

        // Set Rotation Angle
        ObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        ObjectCreator.setVisibility(this, config);
        // Set Renderability
        ObjectCreator.setRenderability(this, config);

        // Set 3D Rotations
        ObjectCreator.set3DRotations(this, config);
        // Set 3D Functions
        ObjectCreator.set3DFunctions(this);

        ObjectCreator.setResolutionListeners(this, true, config);
        ObjectCreator.setOrientationListeners(this, true, config);

        this._planeInfo = {
            fov: 0,
            focalLength: 0,
            nearDistance: config.nearDistance,
            farDistance: config.farDistance
        };

        if (config.fov !== undefined) {
            this._planeInfo.fov = config.fov;
            this._planeInfo.focalLength = Screen.main.size.width / 180 * config.fov;
        } else {
            this._planeInfo.focalLength = config.focalLength;
            this._planeInfo.fov = config.focalLength / Screen.main.size.width * 180;
        }

        this._refreshPlane();

        // It's for Z-index
        this.enableDisplayList = true;
        this.onZOrder = function(item) {
            item.zOrder = item.position.z;
            if (item.zIndex == 2) {
                // It's inner card, and since all matrices are calculated at the point of sorting
                // we can check which side is actually visible
                // if we do that in update(), it will be slower by 1 frame
                item.parent.checkFace();
            }
        };

        if (config.worldPosition !== undefined) {
            this.setWorldPosition(config.worldPosition);
        }

        this._externalBounds = config.externalBounds;

        Camera3D.__main = this;

        config.onInitialize?.bind(this)(config);
    }

    get forward() {
        return Vector3.getObjectForward(this.getRotation());
    }

    dispose() {
        while (this.children.length > 0) {
            this.removeChild(this.children[0]).dispose();
        }
        this.renderable = false;
        this.parentContainer?.removeChild(this);
    }

    // SetParent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.onResolutionChanged();
    }

    __getDepth() {
        // return this._planeInfo.focalLength - (this._planeInfo.focalLength - this._planeInfo.farDistance);
        return -this._planeInfo.focalLength;
    }

    // Get - Set X/Y/Z
    getX() {
        return this.position3d.x;
    }
    getY() {
        return this.position3d.y;
    }
    getZ() {
        return this.position3d.z + this.__getDepth();
    }

    setX(x) {
        this.anchoring.margin.x = x;
        this._refreshPosition();
    }
    setY(y) {
        this.anchoring.margin.y = y;
        this._refreshPosition();
    }
    setZ(z) {
        this.anchoring.margin.z = z;
        this._refreshPosition();
    }

    // Get - Set Position
    getPosition() {
        return {
            x: this.position3d.x,
            y: this.position3d.y,
            z: this.position3d.z + this.__getDepth()
        };
    }
    setPosition(x,y,z) {
        this.anchoring.margin.x = x.x ?? x;
        this.anchoring.margin.y = x.y ?? y;
        this.anchoring.margin.z = x.z ?? z ?? 0;
        this._refreshPosition();
    }

    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return {...this.parentContainer.toGlobal(this.getPosition()), z: this.__getDepth()};
    }
    getWorldPosition() {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        position.z = this.__getDepth();
        return position;
    }
    setRealWorldPosition(x,y,z) {
        let tempZ = x.z ?? z ?? 0;
        this._tPosition.x = x.x ?? x;
        this._tPosition.y = x.y ?? y;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        this._tPosition.z = tempZ - this.parentContainer.getWorldPosition().z;
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;
        this.anchoring.margin.z = this._tPosition.z;

        this._refreshPosition();
    }
    setWorldPosition(x,y,z) {
        let tempZ = x.z ?? z ?? 0;
        this._tPosition.x = (x.x ?? x) + Application.main.worldCenterX;
        this._tPosition.y = (x.y ?? y) + Application.main.worldCenterY;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        this._tPosition.z = tempZ - this.parentContainer.getWorldPosition().z;
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;
        this.anchoring.margin.z = this._tPosition.z;

        this._refreshPosition();
    }

    getWorldPositionScale() {
        return this.worldTransform.mat4[15] / Screen.main.stageScale;
    }
    transformLocalToWorld(value) {
        return value / this.getWorldPositionScale();
    }
    transformLocalToWorldPosition(position) {
        position.x = this.transformLocalToWorld(position.x);
        position.y = this.transformLocalToWorld(position.y);
        if (position.z !== undefined) {
            position.z = this.transformLocalToWorld(position.z);
        }
    }

    _refreshPosition() {
        if (this.anchoring.anchorBounds !== undefined) {
            TransformHandler.updateAnchor3D(this);
        } else {
            TransformHandler.updateMargin3D(this);
        }
    }

    setRotationX(x) {
        this.eulerAngles.x = x;
        this.euler.x = Math.degreesToRadians(x);
    }
    setRotationY(y) {
        this.eulerAngles.y = y;
        this.euler.y = Math.degreesToRadians(y);
    }
    setRotationZ(z) {
        this.eulerAngles.z = z;
        this.euler.z = Math.degreesToRadians(z);
    }
    setRotation(x,y,z) {
        if (x.x !== undefined) {
            z = x.z;
            y = x.y;
            x = x.x;
        }
        this.setRotationX(x);
        this.setRotationY(y);
        this.setRotationZ(z);
    }

    getRotationX() {
        return this.eulerAngles.x;
    }
    getRotationY() {
        return this.eulerAngles.y;
    }
    getRotationZ() {
        return this.eulerAngles.z;
    }
    getRotation() {
        return {...this.eulerAngles};
    }

    setAnchorX(x) {
        this.anchoring.anchor.x = x;
        this._refreshPosition();
    }
    setAnchorY(y) {
        this.anchoring.anchor.y = y;
        this._refreshPosition();
    }
    setAnchor(x,y) {
        this.anchoring.anchor.x = x.x ?? x;
        this.anchoring.anchor.y = x.y ?? y ?? x;
        this._refreshPosition();
    }

    setAnchorBounds(bounds) {
        this.anchoring.anchorBounds = bounds;
        this._refreshPosition();
    }

    // Get - Set Scale
    getScale() {
        return this.scale;
    }
    setScale(x,y) {
        this.scale.set(x, y ?? x);
    }
    setScaleX(x) {
        this.scale.set(x, this.scale.y);
    }
    setScaleY(y) {
        this.scale.set(this.scale.x, y);
    }
    
    get lossyScaleX() {
        return this.scale.x * (this.parentContainer.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this.scale.y * (this.parentContainer.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get boundX() {
        return this.getLocalBounds().width * this.scale.x;
    }
    get boundY() {
        return this.getLocalBounds().height * this.scale.y;
    }
    get bounds() {
        return { width: this.boundX, height: this.boundY };
    }
    get boundWithOffsetX() {
        return this.boundX + this.getLocalBounds().x * this.scale.x;
    }
    get boundWithOffsetY() {
        return this.boundY + this.getLocalBounds().y * this.scale.y;
    }
    get boundsWithOffset() {
        return { width: this.boundWithOffsetX, height: this.boundWithOffsetY };
    }

    get externalBounds() {
        return this._externalBounds ?? (this.parentContainer !== undefined ?
            this.parentContainer.externalBounds : { width: 1, height: 1 });
    }
    set externalBounds(value) {
        this._externalBounds = value;
    }
    get unscaledExternalBounds() {
        return Vector2.multiplySize(this.externalBounds, this.scale.y / this.lossyScale);
    }

    scaleValue(value) {
        return value * this.lossyScale * Screen.main.stageScale;
    }

    onResolutionChanged() {
        if (this.parentContainer === undefined) return;
        
        this._onResolutionWillChangeCallback?.();

        this._refreshPosition();
        this._refreshPlane();

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].onResolutionChanged();
        }

        this._onResolutionChangedCallback?.();
    }

    onOrientationChanged() {
        this._onOrientationWillChangeCallback?.();

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].onOrientationChanged();
        }

        this._onOrientationChangedCallback?.();
    }

    get fov() {
        return this._planeInfo.fov;
    }
    set fov(value) {
        this._planeInfo.fov = value;
        this._planeInfo.focalLength = Screen.main.size.width / 180 * value;
        this._refreshPlane();
    }

    get focalLength() {
        return this._planeInfo.focalLength;
    }
    set focalLength(value) {
        this._planeInfo.focalLength = value;
        this._planeInfo.fov = value / Screen.main.size.width * 180;
        this._refreshPlane();
    }

    get nearDistance() {
        return this._planeInfo.nearDistance;
    }
    set nearDistance(value) {
        this._planeInfo.nearDistance = value;
        this._refreshPlane();
    }

    get farDistance() {
        return this._planeInfo.farDistance;
    }
    set farDistance(value) {
        this._planeInfo.farDistance = value;
        this._refreshPlane();
    }

    _refreshPlane() {
        this.setPlanes(
            this._planeInfo.focalLength,
            this._planeInfo.nearDistance,
            this._planeInfo.farDistance
        );
    }
}